import { useMutation, useQueryClient } from "@tanstack/react-query";
import { albumService } from "../services/albumService";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import { authService } from "../services/authService";
import { albumKeys } from "./useAlbumsQuery";
import { sharedAlbumKeys } from "./useSharedAlbums";
import type { AlbumSharePreview } from "../types/album";

interface UseAlbumShareReturn {
  previewShare: (token: string) => Promise<AlbumSharePreview>;
  createShare: (token: string) => Promise<{ albumId: string; ownerId: string }>;
  isCreating: boolean;
  isPreviewing: boolean;
  error: string | null;
  invalidateAlbums: () => Promise<void>;
}

export const useAlbumShare = (userId: string | undefined): UseAlbumShareReturn => {
  const queryClient = useQueryClient();

  const previewShareMutation = useMutation({
    mutationFn: async (token: string) => {
      if (!userId) {
        throw new Error("User not authenticated");
      }
      await authService.checkAuthentication();
      return albumService.previewAlbumShare(userId, token);
    },
    onError: (error) => {
      console.error("Error previewing album share:", handleApiError(error));
    },
  });

  const createShareMutation = useMutation({
    mutationFn: async (token: string) => {
      if (!userId) {
        throw new Error("User not authenticated");
      }
      await authService.checkAuthentication();
      return albumService.createAlbumShare(userId, token);
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: albumKeys.list(userId) });
        queryClient.invalidateQueries({ queryKey: sharedAlbumKeys.list(userId) });
      }
    },
    onError: (error) => {
      console.error("Error creating album share:", handleApiError(error));
    },
  });

  const invalidateAlbums = async () => {
    if (userId) {
      await queryClient.invalidateQueries({ queryKey: albumKeys.list(userId) });
      await queryClient.invalidateQueries({ queryKey: sharedAlbumKeys.list(userId) });
    }
  };

  return {
    previewShare: previewShareMutation.mutateAsync,
    createShare: createShareMutation.mutateAsync,
    isCreating: createShareMutation.isPending,
    isPreviewing: previewShareMutation.isPending,
    error: createShareMutation.error
      ? getErrorMessage(handleApiError(createShareMutation.error))
      : previewShareMutation.error
        ? getErrorMessage(handleApiError(previewShareMutation.error))
        : null,
    invalidateAlbums,
  };
};
